import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import { ddmmyy } from '@mds/shared/lib/format';
import {
  EVENT_CATEGORIES,
  EVENT_SPACES,
  type AdminEvent,
  type CreateAdminEventRequest,
  type EventCategory,
  type EventSpace,
} from '@mds/shared/types/event';

import { clockRange, keyOf, minutesOf, parseKey, timeValue } from '@mds/shared/lib/dates';
import { formatCents, inputToCents } from '@/lib/money';
import {
  REQUIRED,
  clockTime,
  dollars,
  isoDate,
  optionalText,
  picked,
  requiredText,
  wholeNumberText,
} from '@/lib/validation';

/**
 * The vocabulary the events screens share: how the API's event reads on a row,
 * how the create/edit form maps onto the request, and how its two blocked-action
 * codes are recognised.
 */

/* -------------------------------------------------------------- reading --- */

/** An event's published/full label, with the classes to draw it in. */
export const eventStatusTone = (status: AdminEvent['status']) =>
  status === 'full'
    ? { label: 'Full', cls: 'text-warn-text bg-warn-wash border-warn-edge' }
    : { label: 'Published', cls: 'text-success-text bg-success-wash border-success-edge/35' };

/**
 * Where an event's feedback stands, in one word, with the classes to draw it in.
 * Reads left to right through the life of a form: off, on, invitations sent,
 * responses in. An API built before the summary was added leaves it out, and
 * the row then says nothing rather than something wrong.
 */
export const eventFeedbackTone = (feedback: AdminEvent['feedback'] | undefined) =>
  !feedback
    ? null
    : feedback.responses > 0
      ? {
          label: `${feedback.responses} ${feedback.responses === 1 ? 'response' : 'responses'}`,
          cls: 'text-success-text bg-success-wash border-success-edge/35',
        }
      : feedback.locked
        ? {
            label: 'Invitations sent',
            cls: 'text-success-text bg-success-wash border-success-edge/35',
          }
        : feedback.isEnabled
          ? { label: 'Feedback on', cls: 'text-brand-strong bg-brand-wash border-brand-edge/35' }
          : { label: 'Feedback off', cls: 'text-ink-muted bg-raised border-hairline' };

/** "Thu 17/09/26" — how the programme lists a date. */
export const eventDateLabel = (eventDate: string) => {
  const d = parseKey(eventDate);
  return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${ddmmyy(d)}`;
};

/** "6:00 PM – 8:00 PM" */
export const eventWhen = (event: Pick<AdminEvent, 'startMinute' | 'endMinute'>) =>
  clockRange(event.startMinute, event.endMinute);

/** Zero cents is the programme's "Free", not "$0.00". */
export const eventPriceLabel = (amountMinor: number) =>
  amountMinor === 0 ? 'Free' : formatCents(amountMinor);

/* --------------------------------------------------------------- months --- */

/** A Date as the `yyyy-mm` key the month filter is spelled in. */
export const monthKeyOf = (d: Date) => keyOf(d).slice(0, 7);

/** "2026-09" → "September 2026". */
export const monthLabel = (key: string) => {
  const [year, month] = key.split('-').map(Number);
  return new Date(year ?? 2000, (month ?? 1) - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
};

/**
 * The months the picker offers, from half a year back to a year ahead. The
 * programme is planned a season at a time, so a fixed window around today beats
 * deriving one from whatever the current page of results happens to hold.
 */
export function monthOptions(today = new Date()) {
  return Array.from({ length: 19 }, (_, i) => {
    const key = monthKeyOf(new Date(today.getFullYear(), today.getMonth() - 6 + i, 1));
    return { value: key, label: monthLabel(key) };
  });
}

/* --------------------------------------------------------------- errors --- */

/**
 * The `code` the error middleware puts on every failure, or null when the
 * failure never reached the API.
 */
const errorCode = (error: unknown): string | null =>
  error instanceof ApiError ? (error.code ?? null) : null;

/** A registration is not cancelled, so the record has to stay. Not a failure to retry. */
export const isEventHasRegistrations = (error: unknown) =>
  errorCode(error) === 'EVENT_HAS_REGISTRATIONS';

/** A payment was recorded against it, which the books keep even after a cancellation. */
export const isEventHasPayments = (error: unknown) => errorCode(error) === 'EVENT_HAS_PAYMENTS';
/** The new capacity is below the seats already taken. */
export const isEventCapacityBelowRegistrations = (error: unknown) =>
  errorCode(error) === 'EVENT_CAPACITY_BELOW_REGISTRATIONS';

/* ----------------------------------------------------------------- form --- */

/**
 * One row of the agenda or the good-to-know list while it is being typed.
 *
 * Both are two columns of text in the form and two differently named fields on
 * the wire, so the editor works in pairs and the conversion happens here.
 */
export type LineRow = [string, string];

export interface EventDraft {
  title: string;
  category: EventCategory;
  /** `yyyy-mm-dd`, straight from the date picker. */
  eventDate: string;
  /** "18:00", as a time input holds it. */
  start: string;
  end: string;
  space: EventSpace;
  /** Held as typed so a half-deleted number does not read as NaN. */
  capacity: string;
  /** Dollars as typed. Empty is Free. */
  price: string;
  shortDescription: string;
  longDescription: string;
  priceNote: string;
  agenda: LineRow[];
  goodToKnow: LineRow[];
  /** Stored-file IDs in display order. The first is the cover. */
  mediaFileIds: string[];
}

export const blankEventDraft = (today = new Date()): EventDraft => ({
  title: '',
  category: EVENT_CATEGORIES[0],
  eventDate: keyOf(today),
  start: '18:00',
  end: '20:00',
  space: EVENT_SPACES[0],
  capacity: '40',
  price: '',
  shortDescription: '',
  longDescription: '',
  priceNote: '',
  agenda: [],
  goodToKnow: [],
  mediaFileIds: [],
});

/** An event the API returned, back into the shape the form edits. */
export const draftOf = (event: AdminEvent): EventDraft => ({
  title: event.title,
  category: event.category,
  eventDate: event.eventDate,
  start: timeValue(event.startMinute),
  end: timeValue(event.endMinute),
  space: event.space,
  capacity: String(event.capacity),
  price: event.price.amountMinor === 0 ? '' : (event.price.amountMinor / 100).toFixed(2),
  shortDescription: event.shortDescription,
  longDescription: event.longDescription,
  priceNote: event.priceNote,
  agenda: event.agenda.map(
    (row) =>
      [row.startMinute === null ? '' : timeValue(row.startMinute), row.description] as LineRow,
  ),
  goodToKnow: event.goodToKnow.map((row) => [row.label, row.value] as LineRow),
  mediaFileIds: event.media.map((item) => item.fileId),
});

/** An agenda row is worth sending once it says something; the time is optional. */
const agendaOf = (rows: LineRow[]) =>
  rows
    .filter(([, description]) => description.trim() !== '')
    .map(([at, description]) => ({
      startMinute: at.trim() === '' ? null : minutesOf(at),
      description: description.trim(),
    }));

/** A good-to-know row needs both halves — the API takes neither on its own. */
const goodToKnowOf = (rows: LineRow[]) =>
  rows
    .filter(([label, value]) => label.trim() !== '' && value.trim() !== '')
    .map(([label, value]) => ({ label: label.trim(), value: value.trim() }));

/** Checks a value against one rule and files its first message at `path`. */
function checkWith(
  ctx: z.RefinementCtx,
  path: (string | number)[],
  rule: z.ZodType,
  value: unknown,
) {
  const result = rule.safeParse(value);
  if (!result.success) {
    ctx.addIssue({ code: 'custom', path, message: result.error.issues[0]?.message ?? REQUIRED });
  }
}

/** A dollar price that may be left empty, which is Free. */
const optionalPrice = z.string().superRefine((value, ctx) => {
  if (value.trim() !== '') checkWith(ctx, [], dollars(), value);
});

/**
 * One agenda line: the time is optional, what happens is not. A line left
 * wholly empty is dropped on the way out rather than flagged.
 */
const agendaLine = z.tuple([z.string(), z.string()]).superRefine(([at, what], ctx) => {
  if (at.trim() === '' && what.trim() === '') return;
  if (at.trim() !== '') checkWith(ctx, [0], clockTime, at);
  if (what.trim() === '') {
    ctx.addIssue({ code: 'custom', path: [1], message: 'Say what happens, or remove the line.' });
  } else checkWith(ctx, [1], optionalText(300), what);
});

/** One good-to-know line needs both halves — the API takes neither on its own. */
const goodToKnowLine = z.tuple([z.string(), z.string()]).superRefine(([label, detail], ctx) => {
  if (label.trim() === '' && detail.trim() === '') return;
  if (label.trim() === '') {
    ctx.addIssue({ code: 'custom', path: [0], message: 'Add a label, or remove the line.' });
  } else checkWith(ctx, [0], optionalText(80), label);
  if (detail.trim() === '') {
    ctx.addIssue({ code: 'custom', path: [1], message: 'Add the detail, or remove the line.' });
  } else checkWith(ctx, [1], optionalText(500), detail);
});

/**
 * What the API would reject, checked here so it fails before the round trip.
 * Mirrors `createAdminEventSchema`. `seatsTaken` is the confirmed seats of the
 * event being edited, since the API refuses a capacity below them.
 */
export const eventSchema = (seatsTaken = 0) =>
  z
    .object({
      title: requiredText(160),
      category: picked('Choose a category.'),
      eventDate: isoDate,
      start: clockTime,
      end: clockTime,
      space: picked('Choose a space.'),
      capacity: wholeNumberText({ min: 1, max: 10_000 }),
      price: optionalPrice,
      shortDescription: requiredText(500),
      longDescription: optionalText(20_000),
      priceNote: optionalText(500),
      agenda: z.array(agendaLine).max(30, 'Up to 30 lines.'),
      goodToKnow: z.array(goodToKnowLine).max(30, 'Up to 30 lines.'),
      mediaFileIds: z
        .array(z.string())
        .min(1, 'Pick at least one picture. The first is the cover.')
        .max(20, 'Up to 20 pictures.'),
    })
    .superRefine((draft, ctx) => {
      if (
        clockTime.safeParse(draft.start).success &&
        clockTime.safeParse(draft.end).success &&
        minutesOf(draft.end) <= minutesOf(draft.start)
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['end'],
          message: 'Set an end time after the start.',
        });
      }
      const capacity = Number(draft.capacity);
      if (seatsTaken > 0 && Number.isInteger(capacity) && capacity >= 1 && capacity < seatsTaken) {
        ctx.addIssue({
          code: 'custom',
          path: ['capacity'],
          message: `${seatsTaken} seats are already taken, so enter ${seatsTaken} or more.`,
        });
      }
    });

/**
 * The draft as the API wants it. Check it against `eventSchema` first — this trusts the
 * draft and falls back rather than throwing on a value that never made it past
 * the form.
 */
export const eventRequestOf = (draft: EventDraft): CreateAdminEventRequest => ({
  title: draft.title.trim(),
  category: draft.category,
  eventDate: draft.eventDate,
  startMinute: minutesOf(draft.start),
  endMinute: minutesOf(draft.end),
  space: draft.space,
  capacity: Number(draft.capacity),
  priceAmountMinor: inputToCents(draft.price === '' ? '0' : draft.price) ?? 0,
  shortDescription: draft.shortDescription.trim(),
  longDescription: draft.longDescription.trim(),
  priceNote: draft.priceNote.trim(),
  agenda: agendaOf(draft.agenda),
  goodToKnow: goodToKnowOf(draft.goodToKnow),
  mediaFileIds: draft.mediaFileIds,
});
